import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../config';
import LoadingSpinner from './LoadingSpinner';
import AdminZellePanel from './AdminZellePanel';
import AdminCualencuestaPanel from './AdminCualencuestaPanel';
import AdminOnboardingPanel from './AdminOnboardingPanel';

const AdminPanel = () => {
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [domainStats, setDomainStats] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [economyDashboard, setEconomyDashboard] = useState(null);
  const [bankCycleResult, setBankCycleResult] = useState(null);
  const [runningCycle, setRunningCycle] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/dashboard`);
      setDashboard(response.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('No tienes permisos de administrador');
      } else {
        setError('Error al cargar el panel de administración');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data.users);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/admin/transactions`);
      setTransactions(response.data.transactions);
    } catch (err) {
      console.error('Error fetching transactions:', err);
    }
  };

  const fetchDomainStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/domains/stats`);
      setDomainStats(response.data.domains);
    } catch (err) {
      console.error('Error fetching domain stats:', err);
    }
  };

  const fetchEconomyDashboard = async () => {
    try {
      const response = await axios.get(`${API}/economy/dashboard`);
      setEconomyDashboard(response.data);
    } catch (err) {
      console.error('Error fetching economy dashboard:', err);
    }
  };

  const runBankCycle = async () => {
    setRunningCycle(true);
    try {
      const response = await axios.post(`${API}/economy/admin/run-cycle`);
      setBankCycleResult(response.data);
      fetchEconomyDashboard();
    } catch (err) {
      console.error('Error running bank cycle:', err);
    } finally {
      setRunningCycle(false);
    }
  };

  const fetchUserDetail = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`);
      setSelectedUser(response.data);
    } catch (err) {
      console.error('Error fetching user detail:', err);
    }
  };

  const toggleUserActive = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/toggle-active`);
      fetchUsers();
    } catch (err) {
      console.error('Error toggling user:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'transactions') fetchTransactions();
    if (activeTab === 'domains') fetchDomainStats();
    if (activeTab === 'economy') fetchEconomyDashboard();
  }, [activeTab]);

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: '📊' },
    { id: 'onboarding', name: 'Incorporación', icon: '🌱' },
    { id: 'economy', name: 'Motor Economico', icon: '🏦' },
    { id: 'users', name: 'Usuarios', icon: '👥' },
    { id: 'transactions', name: 'Transacciones', icon: '💸' },
    { id: 'domains', name: 'Dominios', icon: '🧠' },
    { id: 'zelle', name: 'Pagos Zelle', icon: '💵' },
    { id: 'cualencuesta', name: 'CUALencuesta', icon: '📋' }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Panel de Administración</h2>
        <p className="text-gray-600">Gestión global de CUALAPP</p>
      </div>

      {/* Admin Tabs */}
      <div className="flex space-x-2 border-b">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setSelectedUser(null);
            }}
            data-testid={`admin-tab-${tab.id}`}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-500'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.name}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboard && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500">
              <h3 className="text-sm font-medium text-gray-500">Usuarios Totales</h3>
              <p className="text-3xl font-bold text-gray-900">{dashboard.statistics.users.total}</p>
              <p className="text-sm text-green-600">{dashboard.statistics.users.active} activos</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
              <h3 className="text-sm font-medium text-gray-500">Transacciones</h3>
              <p className="text-3xl font-bold text-gray-900">{dashboard.statistics.transactions.total}</p>
              <p className="text-sm text-green-600">{dashboard.statistics.transactions.completed} completadas</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
              <h3 className="text-sm font-medium text-gray-500">Cual-Acciones</h3>
              <p className="text-3xl font-bold text-gray-900">{dashboard.statistics.items.total}</p>
              <p className="text-sm text-gray-500">Items creados por usuarios</p>
            </div>
          </div>

          {/* Volume by Currency */}
          {dashboard.statistics.transactions.volume_by_currency.length > 0 && (
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Volumen por Moneda</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {dashboard.statistics.transactions.volume_by_currency.map((v) => (
                  <div key={v.currency} className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">{v.currency}</p>
                    <p className="text-lg font-bold text-gray-900">{v.total.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">{v.count} txns</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Activity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Actividad Reciente</h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {dashboard.recent_activities.map((activity, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm border-b pb-2">
                    <div>
                      <p className="font-medium text-gray-900">{activity.action}</p>
                      <p className="text-xs text-gray-500">{activity.module}</p>
                    </div>
                    <p className="text-xs text-gray-400">
                      {new Date(activity.timestamp).toLocaleString('es-ES')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Usuarios Recientes</h3>
              <div className="space-y-3">
                {dashboard.recent_users.map((user) => (
                  <div key={user.id} className="flex items-center justify-between text-sm border-b pb-2">
                    <div>
                      <p className="font-medium text-gray-900">{user.full_name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    <p className="text-xs text-gray-400">
                      {new Date(user.created_at).toLocaleDateString('es-ES')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Economy Tab - Motor Economico */}
      {activeTab === 'economy' && (
        <div className="space-y-6">
          {economyDashboard ? (
            <>
              {/* System State Header */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 text-white" data-testid="economy-dashboard">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold">Banco Central CUALAPP</h3>
                    <p className="text-sm text-slate-300">Motor de Regulacion Algoritmica</p>
                  </div>
                  <button
                    onClick={runBankCycle}
                    disabled={runningCycle}
                    data-testid="run-bank-cycle-btn"
                    className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-500 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    {runningCycle ? 'Ejecutando...' : 'Ejecutar Ciclo del Banco Central'}
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white/10 rounded-lg p-3">
                    <p className="text-xs text-slate-300">Supply Total</p>
                    <p className="text-xl font-bold">{(economyDashboard.system_state?.total_supply || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3">
                    <p className="text-xs text-slate-300">Circulante</p>
                    <p className="text-xl font-bold">{(economyDashboard.system_state?.circulating_supply || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3">
                    <p className="text-xs text-slate-300">Quemado</p>
                    <p className="text-xl font-bold text-orange-300">{(economyDashboard.system_state?.burned_total || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3">
                    <p className="text-xs text-slate-300">Precio Ref.</p>
                    <p className="text-xl font-bold text-emerald-300">${economyDashboard.system_state?.reference_price_usd || 0.10} USD</p>
                  </div>
                </div>
              </div>

              {/* Pools Distribution */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Pools de Distribucion (25/25/25/15/10)</h3>
                <div className="space-y-3">
                  {[
                    { key: 'liquidity', name: 'Liquidez Progresiva', color: 'bg-blue-500', pct: 25 },
                    { key: 'rewards', name: 'Recompensas Actividad', color: 'bg-emerald-500', pct: 25 },
                    { key: 'treasury', name: 'Tesoreria CUALAPP', color: 'bg-amber-500', pct: 25 },
                    { key: 'growth', name: 'Incentivos Crecimiento', color: 'bg-purple-500', pct: 15 },
                    { key: 'reserve', name: 'Reserva Estabilidad', color: 'bg-red-500', pct: 10 }
                  ].map(pool => {
                    const value = economyDashboard.pools?.[pool.key] || 0;
                    const total = 1000000000;
                    const pctFilled = (value / (total * pool.pct / 100)) * 100;
                    return (
                      <div key={pool.key} data-testid={`pool-${pool.key}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">{pool.name} ({pool.pct}%)</span>
                          <span className="text-sm text-gray-500">{value.toLocaleString()} CUALcoin</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div className={`${pool.color} h-3 rounded-full transition-all`} style={{ width: `${Math.min(pctFilled, 100)}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Inflation & Velocity */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Inflation Monitor */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Monitor de Inflacion</h3>
                  <div className={`text-center p-3 rounded-lg mb-4 ${
                    economyDashboard.inflation?.system_health === 'ESTABLE' ? 'bg-emerald-50 text-emerald-700' :
                    economyDashboard.inflation?.system_health === 'ALERTA' ? 'bg-amber-50 text-amber-700' :
                    'bg-red-50 text-red-700'
                  }`}>
                    <p className="text-lg font-bold" data-testid="system-health">{economyDashboard.inflation?.system_health || 'N/A'}</p>
                    <p className="text-xs">Salud del Sistema</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Monetaria (pi_m)</span>
                      <span className="text-sm font-mono font-medium">{economyDashboard.inflation?.inflation_monetary ?? 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Poder (pi_p)</span>
                      <span className="text-sm font-mono font-medium">{economyDashboard.inflation?.inflation_power ?? 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Utilidad (pi_u)</span>
                      <span className="text-sm font-mono font-medium">{economyDashboard.inflation?.inflation_utility ?? 0}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-800">Combinada</span>
                      <span className="text-sm font-mono font-bold">{economyDashboard.inflation?.inflation_combined ?? 0}</span>
                    </div>
                  </div>
                </div>

                {/* Velocity */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Velocity (KPI Principal)</h3>
                  <div className="text-center p-3 rounded-lg mb-4 bg-blue-50 text-blue-700">
                    <p className="text-lg font-bold" data-testid="system-value">{(economyDashboard.velocity?.system_value || 0).toLocaleString()}</p>
                    <p className="text-xs">Valor del Sistema (N x V x k)</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Volumen 24h</span>
                      <span className="text-sm font-mono font-medium">{(economyDashboard.velocity?.volume_24h || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Transacciones 24h</span>
                      <span className="text-sm font-mono font-medium">{economyDashboard.velocity?.transactions_24h || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Velocity Ratio</span>
                      <span className="text-sm font-mono font-medium">{economyDashboard.velocity?.velocity_ratio_24h ?? 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Velocity/Usuario</span>
                      <span className="text-sm font-mono font-medium">{economyDashboard.velocity?.velocity_per_user_24h ?? 0}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-800">Multiplicador (k)</span>
                      <span className="text-sm font-mono font-bold">{economyDashboard.velocity?.k_multiplier ?? 1}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Users & Vesting */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl shadow-md p-6 text-center">
                  <p className="text-3xl font-bold text-gray-900">{economyDashboard.users?.total || 0}</p>
                  <p className="text-sm text-gray-500">Usuarios Totales</p>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6 text-center">
                  <p className="text-3xl font-bold text-emerald-600">{economyDashboard.users?.active || 0}</p>
                  <p className="text-sm text-gray-500">Usuarios Activos</p>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6 text-center">
                  <p className="text-3xl font-bold text-purple-600" data-testid="vesting-phase">{economyDashboard.vesting_phase || 'phase_1'}</p>
                  <p className="text-sm text-gray-500">Fase de Vesting</p>
                </div>
              </div>

              {/* Bank Cycle Result */}
              {bankCycleResult && (
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Resultado del Ciclo del Banco Central</h3>
                  <div className="space-y-2">
                    <p className="text-sm"><span className="font-medium">Salud:</span> {bankCycleResult.system_health}</p>
                    <p className="text-sm"><span className="font-medium">Acciones:</span></p>
                    <ul className="list-disc list-inside text-sm text-gray-700">
                      {bankCycleResult.actions_taken?.map((action, i) => (
                        <li key={i}>{action}</li>
                      ))}
                    </ul>
                    <p className="text-sm"><span className="font-medium">Fee actual:</span> {(bankCycleResult.current_config?.fee_rate * 100)?.toFixed(2)}%</p>
                    <p className="text-sm"><span className="font-medium">Limite emision/hora:</span> {bankCycleResult.current_config?.hourly_emission_limit?.toLocaleString()}</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">Cargando datos del Motor Economico...</div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {selectedUser ? (
            <div className="p-6">
              <button
                onClick={() => setSelectedUser(null)}
                className="text-purple-600 hover:text-purple-800 mb-4 flex items-center"
              >
                ← Volver a lista
              </button>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center text-2xl">
                    👤
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{selectedUser.user.full_name}</h3>
                    <p className="text-gray-600">{selectedUser.user.email}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-2">Wallet</h4>
                    {Object.entries(selectedUser.wallet.balances || {}).map(([curr, bal]) => (
                      <p key={curr} className="text-sm">
                        <span className="font-medium">{curr}:</span> {bal.toLocaleString()}
                      </p>
                    ))}
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-2">Estadísticas</h4>
                    <p className="text-sm">Items: {selectedUser.items_count}</p>
                    <p className="text-sm">Transacciones: {selectedUser.transactions.length}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Transacciones Recientes</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedUser.transactions.map((t) => (
                      <div key={t.id} className="text-sm flex justify-between bg-gray-50 p-2 rounded">
                        <span>{t.type} - {t.amount} {t.currency}</span>
                        <span className="text-gray-500">{t.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CUAL Balance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{user.full_name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">{user.cual_balance?.toLocaleString() || 0} CUAL</td>
                    <td className="px-6 py-4 text-sm">{user.items_count}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        user.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active !== false ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 space-x-2">
                      <button
                        onClick={() => fetchUserDetail(user.id)}
                        className="text-purple-600 hover:text-purple-800 text-sm"
                      >
                        Ver
                      </button>
                      <button
                        onClick={() => toggleUserActive(user.id)}
                        className="text-orange-600 hover:text-orange-800 text-sm"
                      >
                        {user.is_active !== false ? 'Desactivar' : 'Activar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">De</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Para</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactions.map((tx) => (
                <tr key={tx.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm">{tx.from_user}</td>
                  <td className="px-6 py-4 text-sm">{tx.to_user}</td>
                  <td className="px-6 py-4 text-sm font-medium">
                    {tx.amount.toLocaleString()} {tx.currency}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      tx.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {tx.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(tx.created_at).toLocaleString('es-ES')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Domains Tab */}
      {activeTab === 'domains' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {domainStats.map((domain) => (
            <div
              key={domain.id}
              className="bg-white rounded-xl shadow-md p-4 border-l-4"
              style={{ borderLeftColor: domain.color }}
            >
              <div className="flex items-center space-x-3 mb-3">
                <span className="text-2xl">{domain.icon}</span>
                <h3 className="font-semibold text-gray-900">{domain.name}</h3>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-gray-50 p-2 rounded text-center">
                  <p className="text-gray-500">Catálogo</p>
                  <p className="font-bold text-gray-900">{domain.catalog_items}</p>
                </div>
                <div className="bg-gray-50 p-2 rounded text-center">
                  <p className="text-gray-500">Creados</p>
                  <p className="font-bold text-purple-600">{domain.user_items}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Zelle Tab */}
      {activeTab === 'zelle' && (
        <AdminZellePanel />
      )}

      {/* CUALencuesta Tab */}
      {activeTab === 'cualencuesta' && (
        <AdminCualencuestaPanel />
      )}

      {/* Onboarding Tab */}
      {activeTab === 'onboarding' && (
        <AdminOnboardingPanel />
      )}
    </div>
  );
};

// Omnibox Component - Barra de Comandos Universal


export default AdminPanel;
